"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import React from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Button } from "../../../components/ui/button"
import { ChevronLeft, ChevronRight, ArrowUpDown, Mail, FileText, MessageSquare, XCircle, Check, FileSpreadsheet, ClipboardList, Truck, Users, FileSearch, IterationCw  } from "lucide-react"
import { Badge } from "../../../components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip"
import { Skeleton } from "../../../components/ui/skeleton"
import { toast } from "react-toastify"
import { subscribeToFlowStatusUpdates, unsubscribeFromFlowStatusUpdates, getWorkflowResultsp2p } from "../../../../ApiCalls/mantisFlowsManangment"

// Helper to generate a unique storage key (optional, but good practice if multiple tables exist)
const getStorageKey = (flowId) => `flowTableColumnOrder_${flowId || 'default'}`;

export default function FlowTable({ 
  data: initialData, 
  visibleColumns, 
  onRowClick, 
  columnLabels, 
  statusIndicator,
  flowId,
  onRefreshData,
  clientId
}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [processingItems, setProcessingItems] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  })
  const itemsPerPage = 10

  // Función para cargar los datos
  const fetchData = async () => {
    if (!clientId) return;
    
    try {
      setLoading(true);
      const response = await getWorkflowResultsp2p(clientId);
      
      if (response.success) {
        // Adaptamos los datos para que coincidan con el formato esperado
        const formattedResults = response.data.map((result) => {
          const formattedResult = { ...result };

          if (result.extraction_date) {
            formattedResult.extraction_date = new Date(
              result.extraction_date
            ).toLocaleString();
          }

          if (result.total_amount) {
            formattedResult.total_amount = `$${parseFloat(
              result.total_amount
            ).toLocaleString("es-CO")}`;
          }

          formattedResult.hasErrors = result.validation_status === "Rechazado";
          formattedResult.isVerified = result.validation_status === "Validado";
          formattedResult.status = mapValidationStatus(result.validation_status);
          formattedResult.date = result.extraction_date;

          return formattedResult;
        });

        setData(formattedResults);
      } else {
        toast.error("Error al cargar los datos");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Función para mapear los estados de validación
  const mapValidationStatus = (status) => {
    switch (status) {
      case "Validado":
        return "complete";
      case "Pendiente":
        return "pending";
      case "Rechazado":
        return "error";
      default:
        return "pending";
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (clientId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // --- Column Reordering State ---
  const [columnOrder, setColumnOrder] = useState(visibleColumns);
  const draggedColumnRef = useRef(null);
  const dragOverColumnRef = useRef(null);

  // --- Load and Validate Column Order on Mount ---
  useEffect(() => {
    const storageKey = getStorageKey(flowId);
    const savedOrderJson = localStorage.getItem(storageKey);
    let initialOrder = [...visibleColumns];

    if (savedOrderJson) {
      try {
        const savedOrder = JSON.parse(savedOrderJson);
        const validSavedOrder = savedOrder.filter(col => visibleColumns.includes(col));
        const missingColumns = visibleColumns.filter(col => !validSavedOrder.includes(col));
        if (validSavedOrder.length > 0) {
           initialOrder = [...validSavedOrder, ...missingColumns];
        }
      } catch (e) {
        console.error("Failed to parse saved column order:", e);
        localStorage.removeItem(storageKey);
      }
    }
    setColumnOrder(initialOrder);
  }, [visibleColumns, flowId]);

  // --- Save Column Order on Change ---
  useEffect(() => {
    if (columnOrder && columnOrder.length > 0 && columnOrder !== visibleColumns) {
       const storageKey = getStorageKey(flowId);
       localStorage.setItem(storageKey, JSON.stringify(columnOrder));
       console.log("Saved column order:", columnOrder);
    }
  }, [columnOrder, flowId, visibleColumns]);

  const handleStatusUpdate = useCallback((eventData) => {
    // Solo procesar actualizaciones de estado si tenemos un flowId
    if (!flowId) return;

    console.log("[SSE Handler] Received update:", eventData);
    
    if (eventData.type !== "status_update") {
      console.log(`[SSE Handler] Ignorando evento de tipo: ${eventData.type}`);
      return;
    }
    
    const identifier = eventData.email;
    
    if (!identifier) {
      console.warn("[SSE Handler] Evento recibido sin identificador de email:", eventData);
      return;
    }

    console.log(`[SSE Handler] Evento de estado para ${identifier}:`, {
      status: eventData.status,
      timestamp: eventData.timestamp,
      hora: new Date(eventData.timestamp).toLocaleTimeString()
    });

    const status = eventData.status;

    switch (status) {
      case 'received':
        console.log(`[SSE Handler] Documento recibido para: ${identifier}`);
        setProcessingItems(prev => new Set(prev).add(identifier));
        toast.info(`Documento de ${identifier} recibido y en cola de procesamiento`);
        break;
      case 'processing':
        console.log(`[SSE Handler] Procesando documento de: ${identifier}`);
        setProcessingItems(prev => new Set(prev).add(identifier));
        toast.info(`Procesando documento de ${identifier}...`);
        break;
      case 'processed':
        console.log(`[SSE Handler] Procesamiento completado para: ${identifier}`);
        setProcessingItems(prev => new Set(prev).add(identifier));
        setTimeout(() => {
          setProcessingItems(prev => {
            const next = new Set(prev);
            next.delete(identifier);
            return next;
          });
        }, 2000);
        toast.success(`Documento de ${identifier} procesado exitosamente!`);
        
        if (flowId) {
          console.log(`[SSE Handler] Actualizando estadísticas para flujo ${flowId}`);
          const statsEvent = new CustomEvent('refreshFlowStats', { 
            detail: { flowId: flowId }
          });
          window.dispatchEvent(statsEvent);
        }
        
        if (onRefreshData) {
          onRefreshData();
        }
        break;
      case 'error':
        console.error(`[SSE Handler] Error procesando documento de: ${identifier}`, eventData.error || '');
        setProcessingItems(prev => {
          const next = new Set(prev);
          next.delete(identifier);
          return next;
        });
        toast.error(`Error procesando documento de ${identifier}: ${eventData.error || 'Error desconocido'}`);
        break;
      default:
        console.log(`[SSE Handler] Estado no manejado: ${status}`);
    }
  }, [flowId, onRefreshData]);

  useEffect(() => {
    let eventSource = null;
    if (flowId) {
      console.log(`[FlowTable Effect] Subscribing to SSE for flowId: ${flowId}`);
      eventSource = subscribeToFlowStatusUpdates(flowId, handleStatusUpdate);
    } else {
      console.log("[FlowTable Effect] No flowId provided, skipping SSE subscription.");
    }

    return () => {
      if (eventSource) {
        console.log(`[FlowTable Effect] Unsubscribing from SSE for flowId: ${flowId}`);
        unsubscribeFromFlowStatusUpdates(eventSource);
      }
    };
  }, [flowId, handleStatusUpdate]);

  const sortedData = useMemo(() => {
    console.log("[FlowTable] Sorting data by:", sortConfig);
    let sortableData = [...data];
    
    if (sortConfig.key) {
      // Definir un mapa de prioridades para 'criticality'
      const criticalityOrder = {
        low: 1,
        medium: 2,
        high: 3,
        baja: 1, // Añadir traducciones si es necesario
        media: 2,
        alta: 3,
      };

      sortableData.sort((a, b) => {
        const key = sortConfig.key;
        let aValue = a[key];
        let bValue = b[key];

        // Manejar valores nulos o indefinidos primero
        if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;

        // Lógica de ordenamiento específica para 'criticality'
        if (key === 'criticality') {
          const aCriticality = String(aValue).toLowerCase();
          const bCriticality = String(bValue).toLowerCase();
          const aOrder = criticalityOrder[aCriticality] || 0; // 0 para valores no mapeados
          const bOrder = criticalityOrder[bCriticality] || 0;

          if (aOrder < bOrder) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aOrder > bOrder) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }

        // Convertir a minúsculas si son strings para ordenamiento insensible a mayúsculas (para otras columnas)
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        
        // Ordenar fechas si la columna contiene fechas
        if (
          (aValue instanceof Date && bValue instanceof Date) ||
          (typeof aValue === 'string' && !isNaN(Date.parse(aValue)) && typeof aValue !== 'number' && // Evitar que números se traten como fechas
           typeof bValue === 'string' && !isNaN(Date.parse(bValue)) && typeof bValue !== 'number')
        ) {
          const aDate = aValue instanceof Date ? aValue : new Date(aValue);
          const bDate = bValue instanceof Date ? bValue : new Date(bValue);
          
          if (aDate.getTime() < bDate.getTime()) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aDate.getTime() > bDate.getTime()) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }
        
        // Ordenamiento numérico si ambos valores son números o pueden convertirse a números
        const numA = Number(aValue);
        const numB = Number(bValue);
        if (!isNaN(numA) && !isNaN(numB)) {
          if (numA < numB) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (numA > numB) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }
        
        // Ordenamiento de strings (localeCompare para mejor manejo de caracteres)
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Ordenamiento general para otros tipos (booleans, etc.)
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
      
      console.log("[FlowTable] Sorted data (first 5 keys):", sortableData.slice(0, 5).map(item => item[sortConfig.key]));
    }
    
    return sortableData;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage)

  const handleSort = useCallback((clickedKey) => {
    console.log("[FlowTable] Sorting by column:", clickedKey);
    setSortConfig(prevConfig => {
      if (prevConfig.key === clickedKey) {
        // La columna ya está seleccionada, ciclar dirección o desactivar
        if (prevConfig.direction === 'asc') {
          return { key: clickedKey, direction: 'desc' };
        } else {
          // Estaba en 'desc', ahora desactivar
          return { key: null, direction: 'asc' }; // Reset sort
        }
      } else {
        // Nueva columna seleccionada, ordenar 'asc'
        return { key: clickedKey, direction: 'asc' };
      }
    });
  }, []);

  const formatCellValue = (key, value) => {
    if (key === "estado") {
      const processedValue = String(value).trim().toLowerCase();
      let badgeClasses = "px-2 py-1 rounded-full text-xs font-medium";

      switch (processedValue) {
        case "clasificado":
          badgeClasses += " bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300";
          break;
        case "asignado":
          badgeClasses += " bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
          break;
        case "resolviendo":
          badgeClasses += " bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
          break;
        case "resuelto":
          badgeClasses += " bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300";
          break;
        case "cerrado":
          badgeClasses += " bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400";
          break;
        default:
          badgeClasses += " bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
      }

      if (!value) {
        return <span className="text-slate-400 dark:text-slate-500">-</span>;
      }

      return (
        <Badge className={badgeClasses}>
          {value}
        </Badge>
      );
    } else if (key === "isVerified") {
      return (
        <Badge
          className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${
              value
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                : "bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
            }
          `}
        >
          {value ? "Verificado" : "No verificado"}
        </Badge>
      );
    } else if (key === "hasErrors") {
      return (
        <Badge
          className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${
              value
                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
            }
          `}
        >
          {value ? "Sí" : "No"}
        </Badge>
      );
    } else if (key === "prioridad" || key === "criticality") {
      const processedValue = String(value).trim();
      let badgeClasses = "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-[80px] text-center";

      if (processedValue === "Alta" || processedValue === "high") {
        badgeClasses += " bg-red-200 text-red-800 dark:bg-red-800/40 dark:text-red-200";
      } else if (processedValue === "Media" || processedValue === "medium") {
        badgeClasses += " bg-amber-200 text-amber-800 dark:bg-amber-700/40 dark:text-amber-200";
      } else if (processedValue === "Baja" || processedValue === "low") {
        badgeClasses += " bg-emerald-200 text-emerald-800 dark:bg-emerald-700/40 dark:text-emerald-200";
      } else {
        if (value) {
          badgeClasses += " bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300";
        }
      }

      if (!value) {
        return <span className="text-slate-400 dark:text-slate-500">-</span>;
      }

      return (
        <span className={badgeClasses}>
          {value}
        </span>
      );
    } else if (key === "date" && value) {
      return (
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {new Date(value).toLocaleDateString()}
        </span>
      );
    } else if ((key === "created_at" || key === "updated_at") && value) {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          console.warn(`Invalid date value for key ${key}:`, value);
          return <span className="text-slate-400 dark:text-slate-500">-</span>;
        }
        const formattedDate = date.toLocaleString(undefined, { 
          year: 'numeric', 
          month: 'long',
          day: 'numeric'
        }); 
        return (
          <span className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
            {formattedDate}
          </span>
        );
      } catch (error) {
        console.error(`Error formatting date for key ${key}:`, value, error);
        return <span className="text-slate-400 dark:text-slate-500">-</span>;
      }
    } else if (key === "correo") {
      return value;
    } else if (/^\d+$/.test(value)) {
      return (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </span>
      );
    } else if (typeof value === "boolean") {
      return (
        <Badge
          className={`
              px-2 py-1 rounded-full text-xs font-medium
              ${
                value
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                  : "bg-red-50 text-red-700 dark:bg-red-800 dark:text-red-400"
              }
            `}
        >
          {value ? "Sí" : "No"}
        </Badge>
      );
    }
    else if (typeof value === "object" && value !== null) {
      return (
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {JSON.stringify(value)}
        </span>
      );
    }
    else if (key === "origin_channel") {
      const processedValue = String(value).trim();
      let badgeClasses = "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-[120px]";

      if (processedValue === "email") {
        badgeClasses += " bg-red-200 text-red-800 dark:bg-red-800/40 dark:text-red-200";
        return (
          <span className={badgeClasses}>
            <Mail className="h-3 w-3 mr-1" />
            Email
          </span>
        );
      } else if (processedValue === "form") {
        badgeClasses += " bg-amber-200 text-amber-800 dark:bg-amber-700/40 dark:text-amber-200";
        return (
          <span className={badgeClasses}>
            <FileText className="h-3 w-3 mr-1" />
            Form
          </span>
        );
      } else if (processedValue === "whatsapp") {
        badgeClasses += " bg-emerald-200 text-emerald-800 dark:bg-emerald-700/40 dark:text-emerald-200";
        return (
          <span className={badgeClasses}>
            <MessageSquare className="h-3 w-3 mr-1" />
            WhatsApp
          </span>
        );
      } else {
        if (value) {
          badgeClasses += " bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300";
          return (
            <span className={badgeClasses}>
              {value}
            </span>
          );
        }
      }
      
      return <span className="text-slate-400 dark:text-slate-500">-</span>;
    } else if (key === "step") {
      return renderStepBadge(value);
    }
    
    return value !== null && value !== undefined ? String(value) : "-";
  }

  const getColumnLabel = useCallback((key) => {
    // Mapeo específico para ciertos nombres de columnas
    const specialColumnMappings = {
      'ai_summary': 'Summary',
      'delivery_target_date': 'Due Date',
      'area': 'Department',
      'id': 'ID',
      'created_by_name': 'Name',
      'created_at': 'Date',
      'updated_at': 'Date',
    };

    // Verificar si existe un mapeo específico para esta clave
    if (specialColumnMappings[key]) {
      return specialColumnMappings[key];
    }

    // Si hay etiquetas personalizadas proporcionadas y esta clave existe en ellas
    if (columnLabels && columnLabels[key]) {
      return columnLabels[key];
    }

    // Formateo predeterminado: convertir snake_case a Title Case
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [columnLabels]);
  
  const renderCellContent = (key, value) => {
    const formattedValue = formatCellValue(key, value);
    
    if (React.isValidElement(formattedValue)) {
      return formattedValue;
    }
    
    if (formattedValue !== undefined && formattedValue !== null) {
      const stringValue = String(formattedValue);
      
      if (stringValue.length > 20) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate block max-w-[150px] font-medium text-slate-900 dark:text-slate-100">
                  {stringValue}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs break-words">{stringValue}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      } else {
        return (
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {stringValue}
          </span>
        );
      }
    }
    
    return <span className="text-slate-500">-</span>;
  };

  
  const identifiersOnPage = new Set(paginatedData.map(row => row.userPhoneNumber || row.id));
  const processingItemsNotOnPage = Array.from(processingItems).filter(id => !identifiersOnPage.has(id));

  // --- Drag and Drop Handlers ---
  
  
  
  
  
  const renderStepBadge = (step) => {
    const processedValue = String(step).trim();
    let badgeClasses = "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-[120px]";
    let icon = null;
    let displayText = processedValue;

    if (!processedValue || processedValue === "null" || processedValue === "") {
      return <span className={badgeClasses}>-</span>;
    }

    switch (processedValue) {
      case "Reject":
        badgeClasses += " bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
        icon = <XCircle className="h-3 w-3 mr-1" />;
        break;
      case "Approve":
        badgeClasses += " bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
        icon = <Check className="h-3 w-3 mr-1" />;
        break;
      case "Requests":
        badgeClasses += " bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
        icon = <FileText className="h-3 w-3 mr-1" />;
        break;
      case "Suppliers":
        badgeClasses += " bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
        icon = <Users className="h-3 w-3 mr-1" />;
        break;
      case "Quotations":
        badgeClasses += " bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
        icon = <FileSpreadsheet className="h-3 w-3 mr-1" />;
        break;
      case "PO Creation":
        badgeClasses += " bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300";
        icon = <ClipboardList className="h-3 w-3 mr-1" />;
        break;
      case "Delivery & Reconciliation":
        badgeClasses += " bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300";
        icon = <Truck className="h-3 w-3 mr-1" />;
        break;
      case "created":
        badgeClasses += " bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
        icon = <FileSearch className="h-3 w-3 mr-1" />;
        displayText = "In Review";
        break;
      case "Review":
          badgeClasses += " bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
          icon = <IterationCw className="h-3 w-3 mr-1" />;
          displayText = "Resubmit";
          break;
      default:
        badgeClasses += " bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
        icon = "-";
        break;
    }

    return (
      <span className={badgeClasses}>
        {icon}
        {displayText}
      </span>
    );
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50">
              {/* Render status indicator header if needed */}
              {statusIndicator && <TableHead className="py-3 px-4 w-[50px]"></TableHead>}

              {/* Render headers based on columnOrder */}
              {columnOrder.map((column) => (
                <TableHead
                  key={column}
                  className="py-3 px-4 text-left cursor-pointer select-none"
                  onClick={() => handleSort(column)}
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', column);
                    draggedColumnRef.current = column;
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    dragOverColumnRef.current = column;
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const draggedColumn = draggedColumnRef.current;
                    const dropColumn = dragOverColumnRef.current;
                    
                    if (draggedColumn && dropColumn && draggedColumn !== dropColumn) {
                      const newOrder = [...columnOrder];
                      const draggedIndex = newOrder.indexOf(draggedColumn);
                      const dropIndex = newOrder.indexOf(dropColumn);
                      
                      newOrder.splice(draggedIndex, 1);
                      newOrder.splice(dropIndex, 0, draggedColumn);
                      
                      setColumnOrder(newOrder);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {getColumnLabel(column)}
                    </span>
                    <div className="flex items-center">
                      {sortConfig.key === column ? (
                        sortConfig.direction === 'asc' ? (
                          <ArrowUpDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-blue-600 dark:text-blue-400 transform rotate-180" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-slate-400 opacity-50" />
                      )}
                    </div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnOrder.length + (statusIndicator ? 1 : 0)} className="text-center py-4">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span className="text-slate-500">Cargando datos...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnOrder.length + (statusIndicator ? 1 : 0)} className="text-center py-4 text-slate-500">
                  No hay datos disponibles
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const rowIdentifier = row.userPhoneNumber || row.id || `row-${index}`;
                const isProcessing = processingItems.has(rowIdentifier);
                
                const rowClasses = `${
                  isProcessing ? 'opacity-60' : (onRowClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50' : '')
                }`;

                return (
                  <TableRow
                    key={rowIdentifier}
                    className={rowClasses}
                    onClick={() => !isProcessing && onRowClick && onRowClick(row)}
                  >
                    {statusIndicator && (
                      <TableCell className="px-4 py-3">
                        {isProcessing 
                          ? <Skeleton className="h-5 w-5 rounded-full animate-pulse" /> 
                          : statusIndicator(row)
                        }
                      </TableCell>
                    )}
                    {columnOrder.map((column) => (
                      <TableCell key={`${rowIdentifier}-${column}`} className={`px-4 py-3 text-sm text-slate-700 dark:text-slate-300`}>
                        {isProcessing ? (
                          <Skeleton className="h-4 w-3/4 animate-pulse" />
                        ) : (
                          renderCellContent(column, row[column])
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {sortedData.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Mostrando {paginatedData.length > 0 ? startIndex + 1 : 0} a {Math.min(startIndex + itemsPerPage, sortedData.length)} de {sortedData.length} resultados
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-8 px-3"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Anterior</span>
            </Button>
            <span className="text-sm px-2 flex items-center text-slate-600 dark:text-slate-400">
               Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="h-8 px-3"
            >
              <span className="sr-only">Siguiente</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

